import React from 'react'
import { useTranslation } from 'react-i18next'
import { Gavel, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  CONTRACT_STATUSES, CONTRACT_STATUS_KEY,
  type ContractAffordances, type ContractFilterState, type ContractGrouping,
} from '../lib/contracts'
import type { CostContractStatus } from '../types'

const ALL = 'all'
const GROUPINGS: ContractGrouping[] = ['levelOne', 'status', 'none']
const GROUPING_KEY: Record<ContractGrouping, string> = {
  levelOne: 'CostControl_contracts_group_by_level_one',
  status: 'CostControl_contracts_group_by_status',
  none: 'CostControl_contracts_group_by_none',
}
const TRIGGER = 'h-8 w-auto min-w-[150px] text-sm'

interface Props {
  count: number
  filter: ContractFilterState
  onFilterChange: (filter: ContractFilterState) => void
  grouping: ContractGrouping
  onGroupingChange: (grouping: ContractGrouping) => void
  levelOne: { id: string; name: string }[]
  af: ContractAffordances
  onCreate: () => void
  onFromTender: () => void
}

const ContractsToolbar: React.FC<Props> = ({ count, filter, onFilterChange, grouping, onGroupingChange, levelOne, af, onCreate, onFromTender }) => {
  const { t } = useTranslation()
  const set = (patch: Partial<ContractFilterState>) => onFilterChange({ ...filter, ...patch })
  return (
    <div className="flex flex-wrap items-center gap-3 py-2">
      <div className="flex items-center gap-1">
        <span className="text-lg font-bold">{count}</span>
        <span className="text-sm text-muted-foreground">{t('Common_total')}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <Select value={filter.rootId ?? ALL} onValueChange={(v) => set({ rootId: v === ALL ? null : v })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_level_one')}</SelectItem>
            {levelOne.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.status ?? ALL} onValueChange={(v) => set({ status: v === ALL ? null : (v as CostContractStatus) })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_statuses')}</SelectItem>
            {CONTRACT_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(CONTRACT_STATUS_KEY[s])}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter.search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder={t('CostControl_contracts_search')}
            className="h-8 w-[200px] pl-8 pr-8 text-sm"
          />
          {filter.search && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => set({ search: '' })}
              aria-label={t('CostControl_contracts_search_clear')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={grouping} onValueChange={(v) => onGroupingChange(v as ContractGrouping)}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            {GROUPINGS.map((g) => <SelectItem key={g} value={g}>{t(GROUPING_KEY[g])}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        {af.fromTender && (
          <Button variant="outline" size="sm" className="h-8" onClick={onFromTender}>
            <Gavel className="h-4 w-4" />
            {t('CostControl_contracts_from_tender')}
          </Button>
        )}
        {af.create && (
          <Button size="sm" className="h-8" onClick={onCreate}>
            <Plus className="h-4 w-4" />
            {t('CostControl_contract_new')}
          </Button>
        )}
      </div>
    </div>
  )
}
export default ContractsToolbar
